/**
 * Module sessions: several live classes per module, each with protected
 * recordings streamed as encrypted HLS. A cohort module's classes are the
 * batch's (`kind` "template"); a 1:1 student's are on their module card
 * (`kind` "topic"), theirs alone. Both are scheduled, listed and recorded
 * the same way.
 */

import api from "../../api";
import { baseURL } from "../../aws-config";

const BASE = "api/sessions";

export { errorMessage } from "../ContentBank/contentBankApi";

const owner = (moduleId, kind) => `${BASE}/${kind === "topic" ? "topics" : "modules"}/${moduleId}/`;

export const listSessions = async (moduleId, kind = "template") => (await api.get(owner(moduleId, kind))).data;

/** One session plus its module's other sessions (the session page). */
export const getSession = async (sessionId) => (await api.get(`${BASE}/${sessionId}/`)).data;

/** { title, date: "YYYY-MM-DD", time: "HH:MM", duration: minutes } */
export const scheduleSession = async (moduleId, body, kind = "template") => (await api.post(owner(moduleId, kind), body)).data;

export const addPastSession = async (moduleId, body, kind = "template") =>
  (await api.post(`${owner(moduleId, kind)}past/`, body)).data;

export const updateSession = async (sessionId, body) => (await api.patch(`${BASE}/${sessionId}/`, body)).data;

export const deleteSession = async (sessionId) => (await api.delete(`${BASE}/${sessionId}/`)).data;

export const getUploadUrl = async (sessionId, file) =>
  (await api.post(`${BASE}/${sessionId}/upload-url/`, { filename: file.name, content_type: file.type || "video/mp4" })).data;

export const registerRecording = async (sessionId, key, name) =>
  (await api.post(`${BASE}/${sessionId}/recordings/`, { key, name })).data;

export const getViewers = async (sessionId) => (await api.get(`${BASE}/${sessionId}/viewers/`)).data.viewers;

export const deleteRecording = async (recordingId) => (await api.delete(`${BASE}/recordings/${recordingId}/`)).data;

export const reprocessRecording = async (recordingId) => (await api.post(`${BASE}/recordings/${recordingId}/reprocess/`)).data;

export const getPlaybackToken = async (recordingId) => (await api.get(`${BASE}/recordings/${recordingId}/token/`)).data.token;

export const playlistUrl = (recordingId) => `${baseURL}${BASE}/recordings/${recordingId}/playlist/`;

export const apiOrigin = new URL(baseURL).origin;

export const accessToken = () => localStorage.getItem("access_token") || "";

/** Progress beats; `keepalive` lets the last one leave while the page closes. */
export const sendProgress = (recordingId, position, spans, keepalive = false) =>
  fetch(`${baseURL}${BASE}/recordings/${recordingId}/progress/`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${accessToken()}` },
    body: JSON.stringify({ position, spans }),
    keepalive,
  }).catch(() => {});

export { uploadFile } from "../ContentBank/contentBankApi";

// ─── The batch AI tutor (ask about this module's classes and reading) ─────────

const TUTOR = (sessionId) => `${BASE}/${sessionId}/tutor`;

/** { message, player?: {recording_id, t}, image_data?, request_id } → { job, state, position } */
export const askTutor = async (sessionId, body) => (await api.post(`${TUTOR(sessionId)}/chat/`, body)).data;

export const getTutorJob = async (sessionId, job) =>
  (await api.get(`${TUTOR(sessionId)}/chat/jobs/${encodeURIComponent(job)}/`)).data;

/** { messages: [{role, content, timestamp, i, player}], has_more, offline? } */
export const getTutorHistory = async (sessionId, before = null, limit = 100) =>
  (await api.get(`${TUTOR(sessionId)}/chat/`, { params: { limit, ...(before !== null ? { before } : {}) } })).data;

export const clearTutor = async (sessionId) => (await api.delete(`${TUTOR(sessionId)}/chat/`)).data;

export const getTutorStatus = async (sessionId) => (await api.get(`${TUTOR(sessionId)}/status/`)).data;

/** Mentor: every student of the batch and how much each has asked. */
export const getTutorStudents = async (sessionId) => (await api.get(`${TUTOR(sessionId)}/students/`)).data.students;

export const getStudentTutorHistory = async (sessionId, userId, before = null) =>
  (await api.get(`${TUTOR(sessionId)}/students/${userId}/chat/`, { params: before !== null ? { before } : {} })).data;

// ─── Side conversations: tabs branched from a passage of an answer (backend sessions/threads.py) ───

export const getTutorThreads = async (sessionId) => (await api.get(`${TUTOR(sessionId)}/threads/`)).data.threads;
export const createTutorThread = async (sessionId, body) => (await api.post(`${TUTOR(sessionId)}/threads/`, body)).data;
export const updateTutorThread = async (sessionId, threadId, patch) =>
  (await api.patch(`${TUTOR(sessionId)}/threads/${threadId}/`, patch)).data;
export const askTutorThread = async (sessionId, threadId, body) =>
  (await api.post(`${TUTOR(sessionId)}/threads/${threadId}/chat/`, body)).data;
export const getTutorThreadHistory = async (sessionId, threadId, before = null, limit = 100) =>
  (await api.get(`${TUTOR(sessionId)}/threads/${threadId}/chat/`, { params: before === null ? { limit } : { before, limit } })).data;
export const getStudentTutorThreads = async (sessionId, userId) =>
  (await api.get(`${TUTOR(sessionId)}/students/${userId}/threads/`)).data.threads;
export const getStudentTutorThreadHistory = async (sessionId, userId, threadId, before = null) =>
  (await api.get(`${TUTOR(sessionId)}/students/${userId}/threads/${threadId}/chat/`, { params: before === null ? {} : { before } })).data;
